import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';

import StyledInfo from 'components/common/info';
import useIsMobile from 'hooks/use-is-mobile';
import { typeStyle } from 'constants/typography';

// Was 30px, which has no step on the scale; 32 is the nearest.
const DEFAULT_FIELD_SPACE = 'var(--scale-800)';

/**
 * The modal's subtitle line (`modal.tsx`), and one body paragraph in `silence-insight-modal.tsx`.
 */
const Explanation = styled.div`
  ${typeStyle.bodyMd}
  color: var(--text-body);
`;

const FieldRow = styled.div<{ addMargin?: boolean; zeroTopMargin?: boolean; zeroBottomMargin?: boolean }>`
  display: flex;
  width: 100%;
  ${({ addMargin }) => addMargin && `margin-right: ${DEFAULT_FIELD_SPACE};`}
  ${({ zeroTopMargin }) => zeroTopMargin && 'margin-top: 0;'}
  ${({ zeroBottomMargin }) => zeroBottomMargin && 'margin-bottom: 0;'}
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
`;

const FieldAndLabel = styled.div<{ width?: number; space?: number; height?: number; position?: string }>`
  width: ${({ width }) => (width ? width + '%' : '100%')};
  height: ${({ height }) => (height ? height + '%' : 'auto')};
  position: ${({ position }) => position || 'inherit'};
  display: flex;
  flex-direction: column;

  /* Figma's Text area (181:4446) pads its control by Padding/xxs. */
  textarea {
    height: 100%;
    padding-top: var(--padding-xxs);
    padding-bottom: var(--padding-xxs);
  }

  &:not(:first-of-type) {
    margin-left: ${props => (props.hasOwnProperty('space') ? `${props.space}px` : DEFAULT_FIELD_SPACE)};
  }
`;

const StyledLabel = styled.label`
  /* Flex, not block: the optional suffix is a sibling of the flex text row, so under block it dropped
     to its own line and sat flush on the field below (UI-polish review, "Create New Project"). Figma's
     Input Label Vertical draws label, suffix and marker on one line. */
  ${typeStyle.labelMd}
  color: var(--component-field-label);
  display: flex;
  align-items: baseline;
  gap: var(--spacing-xxxs);
  /* Figma's Input (181:4434): Spacing/xxs above the field, the text inset by Padding/xxxxs. Figma pads the
     far end too, but its info icon sits inside the label row; here the icon is the next sibling, so end
     padding would only widen the gap to it. */
  padding-left: var(--padding-xxxxs);
  margin-bottom: var(--spacing-xxs);
`;

const LabelTextRow = styled.div`
  display: flex;
`;

const OptionalLabel = styled.span`
  color: var(--text-secondary);
`;

const Label = ({
  id,
  text,
  values,
  optional,
  mandatory,
  ...props
}: {
  id?: string;
  text?: string;
  values?: Record<string, any>;
  optional?: boolean;
  mandatory?: boolean;
}) => (
  <StyledLabel {...props}>
    <LabelTextRow>
      {text ? text : <FormattedMessage id={id!} values={values} />}
      {mandatory && <div data-e2e={`${id || text}.mandatory`}>&nbsp;*</div>}
    </LabelTextRow>
    {optional && (
      <OptionalLabel>
        <FormattedMessage id="templates.add.new.optional" />
      </OptionalLabel>
    )}
  </StyledLabel>
);

export interface LabelTextProps {
  id: string;
  values?: Record<string, any>;
  info?: InfoProps;
  'data-e2e'?: string;
}

export interface InfoProps {
  id?: string;
  values?: Record<string, any>;
}

const LabelText = ({ id, values, info, 'data-e2e': dataE2e }: LabelTextProps) => (
  <span data-e2e={dataE2e}>
    <FormattedMessage id={id} values={values} />
    {info && (
      <Info
        information={<FormattedMessage {...info} />}
        onClick={e => {
          e.preventDefault();
        }}
      />
    )}
  </span>
);

const DesktopLabelText = (props: LabelTextProps) => {
  const { isDesktop } = useIsMobile();
  const { info, ...restProps } = props;
  return <LabelText {...restProps} info={isDesktop ? info : undefined} />;
};

const LabelAndIcon = styled.div`
  display: flex;
  align-items: center;
`;

// Was an inline style; as a class it composes with a caller's styled(Info).
const InfoIcon = styled(StyledInfo, {
  shouldForwardProp: prop => prop !== 'spaced' && prop !== 'alignTop'
})<{ spaced: boolean; alignTop: boolean }>`
  ${({ spaced, alignTop }) =>
    spaced &&
    css`
      /* Figma's Input Label Vertical (170:2322) sets its tooltip icon Space/Margin/marginXXS after the text. */
      margin-left: var(--spacing-xxxs);
      ${alignTop && 'margin-bottom: auto;'}
    `}
`;

const Info = ({
  information,
  withoutStyle,
  alignTop = true,
  className,
  onClick
}: {
  information: string | React.ReactNode;
  withoutStyle?: boolean;
  alignTop?: boolean;
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
  <InfoIcon
    spaced={!withoutStyle}
    alignTop={alignTop}
    onClick={onClick}
    className={className}
    tooltip={<TooltipMessage>{information}</TooltipMessage>}
  />
);

const TooltipMessage = styled.div`
  white-space: pre-line;
`;

const ErrorMessage = styled.div<{ errorShouldNotTakeSpace?: boolean; hideWhenEmpty?: boolean }>`
  ${typeStyle.captionMd}
  /* Figma's Input error state (181:4410): Spacing/xxxs under the field, flush with its left edge. */
  margin: var(--spacing-xxxs) 0 0;
  color: var(--component-field-text-error);
  /* Reserved so the field does not jump when a message appears. */
  min-height: var(--scale-500);

  ${({ errorShouldNotTakeSpace }) => errorShouldNotTakeSpace && 'position: absolute; top: 100%;'}
  ${({ hideWhenEmpty }) => hideWhenEmpty && '&:empty { display: none; }'}
`;

const CommonErrorMessage = styled.div`
  height: var(--scale-600);
  ${typeStyle.captionMd}
  color: var(--surface-error-action);
`;

const Container = styled.div`
  display: grid;
  grid-row-gap: var(--spacing-xs);
  padding: 0 var(--spacing-xs);
`;

// No row gap: the label's margin and the error line's carry Figma's spacing, and a gap here added to both.
const Field = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  align-items: center;
  ${typeStyle.bodyMd}
`;

const EllipsisContainer = styled.div<{ maxWidth?: number }>`
  max-width: ${({ maxWidth }) => maxWidth + 'px'};
  width: max-content;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

export {
  Explanation,
  ErrorMessage,
  CommonErrorMessage,
  Info,
  LabelAndIcon,
  Label,
  OptionalLabel,
  StyledLabel,
  FieldAndLabel,
  Row,
  FieldRow,
  Container,
  Field,
  EllipsisContainer,
  LabelText,
  DesktopLabelText
};
